<template>
    <div>
        <CostTypesModal @costTypeCreated="onCostTypeCreated" @costTypeUpdated="onCostTypeUpdated" :show-modal-on="['CostTypesReport']" />

        <TitleBar>
            <template v-slot:title><translate>Cost Categories</translate></template>
            <template v-slot:buttons>
                <GekkoButton 
                    :text="$gettext('Cost types')"
                    :to="{name: 'CostTypesReport'}"
                    icon="fa fa-shopping-cart"
                    variant="outline-primary"/>
                <GekkoButton 
                    :text="$gettext('All reports')"
                    :to="{ name: 'Reports' }"
                    icon="fa fa-chart-column"
                    class="ml-2"/>
            </template>
        </TitleBar>

        <div class="content">
            <div v-if="!$store.getters['getIsPremium']" class="gk-tip gk-tip--warning gk-tip--caution my-4" :style="tipCssVars">
                <span v-translate>This report is based on fake data, please feel free to play with it and </span>
                <a href="/en/subscriptions/manage/">{{ $gettext("upgrade to premium.") }}</a>
            </div>

            <FilterBar
                cookieName="CostCategoriesFilterOptionsVue"
                :mainFilters="[ 'CostCategoriesYearSelector']"
                filterBarType="cost-categories"
                @change="filtersChanged">
                <template v-slot:right-bar>
                    <GekkoButton @click="download" :text="$gettext('download csv')" icon="fa fa-download" variant="link" />
                </template>
            </FilterBar>

            <CostCategoriesTable :costTypes="costTypes" :loading="loading" :totalColText="year"/>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex"

import TitleBar from "@/components/TitleBar"
import GekkoButton from "@/components/GekkoButton"
import CostCategoriesTable from "@/components/cost_types/CostCategoriesTable.vue"
import CostTypeService from "@/store/services/costtype.service"
import FilterBar from "@/components/list_filters/FilterBar"
import CostTypesModal from "@/components/balance_sheet/CostTypesModal"

export default {
    name: "CostCategories",
    components: { TitleBar, GekkoButton, CostCategoriesTable, FilterBar, CostTypesModal },
    data() {
        return {
            filters: {},
            costTypes: [],
            loading: true,
            year: (new Date()).getFullYear().toString()
        }
    },
    computed: {
        ...mapGetters({
            currentCompany: "main/getCurrentCompany"
        }),
        tipCssVars() {
            return `--cautiontext: '${ this.$gettext('Gekko premium tip') }'`
        }
    },
    methods: {
        getCostTypeData() {
            this.loading = true
            this.costTypes = []

            if (this.$store.getters['getIsPremium']) {
                CostTypeService.getCostCategoriesForReport({ year: this.year, company_profile: this.currentCompany.id }).then((response) => {
                    this.costTypes = response.data.cost_types.map((costType) => {
                        let total = 0
                        Object.keys(response.data.totals_by_type).forEach((costTypeId) => {
                            if (parseInt(costTypeId) === costType.id) total = response.data.totals_by_type[costTypeId]
                        })

                        return {'id': costType.id, 'name': costType.name, 'vat_deductible': costType.vat_deductible, 'total': total}
                    })

                    let q = 0
                    response.data.costs_by_type.forEach((cost) => {
                        
                        Object.keys(cost).forEach((costTypeId) => {
                            if (this.costTypes.find((costType) => costType.id === parseInt(costTypeId))) {
                                let costType = this.costTypes.find((costType) => costType.id === parseInt(costTypeId))
                                costType[`q${q+1}`] = cost[costTypeId]
                            }
                        })
                        
                        q++
                    })
                    
                    this.loading = false
                })
            } else {
                this.costTypes.push({id: 0, name: this.$gettext('Materials'), vat_deductible: false, q1: 50, q2: 100, q3: 150, q4: 200, total: 500})
                this.costTypes.push({id: 1, name: this.$gettext('Car costs'), vay_deductible: false, q1: 10, q2: 20, q3: 50, q4: 100, total: 180})
                this.costTypes.push({id: 2, name: this.$gettext('Travel costs (non car)'), vay_deductible: false, q1: 350, q2: 3100, q3: 3150, q4: 3200, total: 12500})
                this.costTypes.push({id: 3, name: this.$gettext('Representational gifts'), vat_deductible: false, q1: 50, q2: 100, q3: 150, q4: 200, total: 500})
                this.costTypes.push({id: 4, name: this.$gettext('Food and beverages (non-catered)'), vay_deductible: false, q1: 10, q2: 20, q3: 50, q4: 100, total: 180})
                this.costTypes.push({id: 5, name: this.$gettext('Food and beverages (catered)'), vay_deductible: false, q1: 350, q2: 3100, q3: 3150, q4: 3200, total: 12500})
                this.costTypes.push({id: 6, name: this.$gettext('Office and administrative costs'), vat_deductible: false, q1: 50, q2: 100, q3: 150, q4: 200, total: 500})
                this.costTypes.push({id: 7, name: this.$gettext('Personal costs'), vay_deductible: false, q1: 10, q2: 20, q3: 50, q4: 100, total: 180})
                this.costTypes.push({id: 8, name: this.$gettext('Other costs'), vay_deductible: false, q1: 350, q2: 3100, q3: 3150, q4: 3200, total: 12500})
                this.loading = false
            }
        },
        filtersChanged(filters) {
            if (filters.cost_categories_year) this.year = filters.cost_categories_year
            this.getCostTypeData()
        },
        onCostTypeCreated() {
            this.getCostTypeData()
        },
        onCostTypeUpdated() {
            this.getCostTypeData()
        },
        download() {
            if (this.$store.getters['getIsPremium']) {
                var fileName = "Financial_details.csv"
                CostTypeService.ExportDetailsCSV({year: this.year, company_profile: this.currentCompany.id}).then((response)=>{
                    const blob = new Blob([response.data], { type: "text/csv" });
                    // Generate a URL for the Blob
                    const blobUrl = URL.createObjectURL(blob);
                    // Create a link element
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = fileName;

                    // Append the link to the body (not required to be visible)
                    document.body.appendChild(link);

                    // Trigger the download
                    link.click();

                    // Cleanup
                    document.body.removeChild(link);
                    URL.revokeObjectURL(blobUrl);
                })
            } else {
                this.$store.commit('togglePremiumModal', 'web_reports')
            }
        }
    }
}
</script>